import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import styles from './button.module.scss'

const iconSizeMap = {
  s: 20,
  m: 24,
  l: 24,
}

const Button = ({
  children = null,
  component,
  disabled = false,
  size = 'm',
  variant = 'default',
  bgColor = null,
  color = null,
  borderColor = null,
  colorHover = null,
  bgColorHover = null,
  borderColorHover = null,
  colorActive = null,
  bgColorActive = null,
  borderColorActive = null,
  startIcon = null,
  endIcon = null,
  fullWidth = false,
  className = '',
  noWrap = false,
  noHover = false,
  style = {},
  grey = false,
  link = false,
  opacity = 100,
  additionalIcon = null,
  noLabel = false,
  ...rest
}) => {
  const roundStyle = 'squared'
  const Component = component || 'button'

  const LabelWrapper = () =>
    !noLabel && children ? (
      <span className={styles['button__label-wrapper']}>{children}</span>
    ) : null

  return (
    <Component
      className={clsx(
        'button',
        styles['button'],
        styles[`button_size-${size}`],
        styles[`button_variant-${variant}-${size}`],
        styles[`button_${roundStyle}`],
        styles[`button_${roundStyle}-${size}`],
        {
          [styles[`button_variant-${variant}`]]: !link && !grey,
          [styles['button_has-adornment']]: startIcon || endIcon,
          [styles['button_has-icon']]: startIcon || endIcon,
          [styles[`button_view-link`]]: link,
          [styles[`button_variant-${variant}-link`]]: link,
          [styles[`button_variant-${variant}-grey`]]: grey,
          [styles[`button_view-grey`]]: grey,
          [styles['button_fluid']]: fullWidth,
          [styles['button_disabled']]: disabled,
          [styles['button_nowrap']]: noWrap,
          [styles['button_has-hover']]: !noHover,
          [styles['button_not-hover']]: noHover,
          [styles['button_with-opacity']]: +opacity < 100,
          [styles['button_as-icon']]:
            (noLabel || !children) && (startIcon || endIcon),
          [styles['button__has-additional-icon']]: !!additionalIcon,
        },
        className,
      )}
      disabled={disabled}
      style={{
        ...style,
        '--color': disabled
          ? `var(--${variant}-color-disabled)`
          : color && `var(--color-${color})`,
        '--color-hover':
          (colorHover && `var(--color-${colorHover})`) ||
          (color && `var(--color-${color}-hover)`),
        '--color-active':
          (colorActive && `var(--color-${colorActive})`) ||
          (color && `var(--color-${color}-active)`),
        '--background-color': bgColor && `var(--color-${bgColor})`,
        '--background-color-hover':
          (bgColorHover && `var(--color-${bgColorHover})`) ||
          (bgColor && `var(--color-${bgColor}-hover)`),
        '--background-color-active':
          (bgColorActive && `var(--color-${bgColorActive})`) ||
          (bgColor && `var(--color-${bgColor}-active)`),
        '--border-color': borderColor && `var(--color-${borderColor})`,
        '--border-color-hover':
          borderColorHover && `var(--color-${borderColorHover})`,
        '--border-color-active':
          borderColorActive && `var(--color-${borderColorActive})`,
        '--opacity': opacity + '%',
      }}
      {...rest}
    >
      {!!startIcon && (
        <span className={styles['button__start-icon-wrapper']}>
          {React.cloneElement(startIcon, {
            ...startIcon.props,
            width: iconSizeMap[startIcon?.props?.size || size],
          })}
        </span>
      )}
      {startIcon || endIcon ? (
        <LabelWrapper>{children}</LabelWrapper>
      ) : (
        children
      )}
      {!!endIcon && (
        <span className={styles['button__end-icon-wrapper']}>
          {React.cloneElement(endIcon, {
            ...endIcon.props,
            width: iconSizeMap[endIcon?.props?.size || size],
          })}
        </span>
      )}
      {!!additionalIcon &&
        React.cloneElement(additionalIcon, {
          size: additionalIcon.props.size || size,
          className: clsx(
            additionalIcon.props.className,
            styles['button__additional-icon'],
          ),
        })}
    </Component>
  )
}

Button.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  startIcon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.oneOf([null]),
  ]),
  endIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  variant: PropTypes.oneOf([
    // @deprecated
    'outlined',
    // @deprecated
    'default',
    // @deprecated
    'contained',
    // @deprecated
    'link',
    'primary',
    'secondary',
    'ghost',
    'error',
    'onsurface',
    'contrast',
  ]),
  color: PropTypes.string, //PropTypes.oneOf(colors),
  textColor: PropTypes.string, //PropTypes.oneOf(colors),
  bgColor: PropTypes.string, //PropTypes.oneOf(colors),
  borderColor: PropTypes.string, //PropTypes.oneOf(colors),
  colorHover: PropTypes.string, //PropTypes.oneOf(colors),
  bgColorHover: PropTypes.string, //PropTypes.oneOf(colors),
  borderColorHover: PropTypes.string, //PropTypes.oneOf(colors),
  colorActive: PropTypes.string, //PropTypes.oneOf(colors),
  bgColorActive: PropTypes.string, //PropTypes.oneOf(colors),
  borderColorActive: PropTypes.string, //PropTypes.oneOf(colors),
  size: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xxl']),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  noHover: PropTypes.bool,
  component: PropTypes.elementType,
  grey: PropTypes.bool,
  link: PropTypes.bool,
  opacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  noWrap: PropTypes.bool,
  style: PropTypes.object,
  additionalIcon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.oneOf([null]),
    PropTypes.bool,
  ]),
  noLabel: PropTypes.bool,
}

export default Button
